import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearch } from "@tanstack/react-router";
import { useMutation, useQuery } from "convex/react";
import {
  Plus,
  MoreHorizontal,
  GitBranch,
  Folder,
  Clock,
  Settings,
  Users,
  LogOut as LeaveIcon,
  Loader2,
  AlertTriangle,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { api } from "@/lib/convex-api";
import { newProjectRoute, projectRoute } from "@/lib/app-routes";
import {
  formatTrialRemaining,
  isOrgBillingActive,
  trialDaysLeft,
} from "@/lib/billing-utils";
import { formatUserFacingError } from "@/lib/errorMessages";
import { organizationContext } from "@/lib/organization-context";
import {
  getProjectRemoteLabel,
  getProjectSourceLabel,
  isLocalProject,
} from "@/features/project-source/source-helpers";
import { ProjectSettings } from "@/features/settings/project-settings";
import { toast } from "sonner";
import type { Id } from "../../../../backend/convex/_generated/dataModel";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatRelativeTime(timestamp: number | undefined): string {
  if (!timestamp) return "Never";
  const diff = Date.now() - timestamp;
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 1) return "Just now";
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? "" : "s"} ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`;
  const days = Math.floor(hours / 24);
  return `${days} day${days === 1 ? "" : "s"} ago`;
}

// ---------------------------------------------------------------------------
// Components
// ---------------------------------------------------------------------------

interface ProjectInfo {
  id: string;
  slug?: string;
  organizationSlug?: string;
  sourceType?: "GITHUB" | "CLOUD" | "GOOGLE_DRIVE";
  source?: {
    provider?: "GITHUB" | "CLOUD" | "GOOGLE_DRIVE";
    label?: string;
    summary?: string | null;
    remoteLabel?: string | null;
  } | null;
  name: string;
  repoOwner: string | null;
  repoName: string | null;
  rootFolder: string;
  lastSyncedAt?: number;
  createdAt: number;
  role: string;
  organizationId?: string;
}

const ProjectCard = ({
  project,
  onOpenSettings,
  onLeaveProject,
}: {
  project: ProjectInfo;
  onOpenSettings: (projectId: string) => void;
  onLeaveProject: (projectId: string) => void;
}) => {
  const isNonOwner = project.role !== "owner";
  const isLocal = isLocalProject(project);
  const sourceLabel = getProjectSourceLabel(project);
  const remoteLabel = getProjectRemoteLabel(project);
  const lastActivity = project.lastSyncedAt ?? project.createdAt;

  return (
    <div className="group relative">
      <Link
        to={projectRoute}
        params={{
          orgSlug: project.organizationSlug ?? "",
          id: project.slug ?? "",
        }}
        className="block"
      >
        <Card className="gap-0 py-0 transition-colors hover:border-foreground/20">
          <CardContent className="px-4 py-4">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1 space-y-2">
                <div className="flex items-center gap-2">
                  <h3 className="truncate text-sm font-semibold">
                    {project.name}
                  </h3>
                  <Badge variant="outline" className="text-[10px] leading-none">
                    {sourceLabel}
                  </Badge>
                  {isNonOwner && (
                    <Badge
                      variant="secondary"
                      className="text-[10px] leading-none capitalize"
                    >
                      {project.role}
                    </Badge>
                  )}
                </div>
                <div className="space-y-1">
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <GitBranch className="size-3 shrink-0" />
                    <span className="truncate">
                      {isLocal
                        ? "Cloud-backed sync source"
                        : (remoteLabel ?? "Remote unavailable")}
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <Folder className="size-3 shrink-0" />
                    <span className="truncate">{project.rootFolder}</span>
                  </div>
                </div>
                <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground/70">
                  <Clock className="size-3 shrink-0" />
                  <span>
                    {project.lastSyncedAt
                      ? `Last synced: ${formatRelativeTime(project.lastSyncedAt)}`
                      : `Created: ${formatRelativeTime(project.createdAt)}`}
                  </span>
                </div>
              </div>
              <div onClick={(e) => e.preventDefault()} className="shrink-0">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon-xs"
                      className="opacity-0 transition-opacity group-hover:opacity-100 data-[state=open]:opacity-100"
                    >
                      <MoreHorizontal className="size-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-44">
                    {!isNonOwner && (
                      <DropdownMenuItem
                        onClick={() => onOpenSettings(project.id)}
                      >
                        <Settings className="mr-2 size-3.5" />
                        Project Settings
                      </DropdownMenuItem>
                    )}
                    {!isNonOwner && (
                      <DropdownMenuItem>
                        <Users className="mr-2 size-3.5" />
                        Team Management
                      </DropdownMenuItem>
                    )}
                    {isNonOwner && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={() => onLeaveProject(project.id)}
                          className="text-destructive focus:text-destructive"
                        >
                          <LeaveIcon className="mr-2 size-3.5" />
                          Leave Project
                        </DropdownMenuItem>
                      </>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          </CardContent>
        </Card>
      </Link>
    </div>
  );
};

const EmptyState = ({
  licensed,
  organizationSlug,
}: {
  licensed: boolean;
  organizationSlug: string | null;
}) => {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <div className="mb-4 flex size-16 items-center justify-center rounded-2xl bg-muted">
        <Folder className="size-7 text-muted-foreground" />
      </div>
      <h3 className="text-lg font-semibold">No projects yet</h3>
      <p className="mt-1 max-w-sm text-sm text-muted-foreground">
        {licensed
          ? "Create your first project to start editing docs."
          : "Subscribe or start a trial to create your first project."}
      </p>
      {licensed && (
        <Button asChild className="mt-6">
          <Link
            to={newProjectRoute}
            params={{ orgSlug: organizationSlug ?? "" }}
          >
            <Plus className="mr-2 size-4" />
            Create Project
          </Link>
        </Button>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export const DashboardPage = () => {
  const leaveProject = useMutation(api.projects.leaveProject);
  const { orgSlug } = useParams({ from: "/_authenticated/$orgSlug" });
  const search = useSearch({ from: "/_authenticated/$orgSlug/" });
  const organizations = useQuery(api.organizations.listForUser, {});
  const [selectedOrganizationId, setSelectedOrganizationId] = useState<
    string | null
  >(organizationContext.getSelectedOrganizationId());
  const selectedOrganization = useMemo(
    () =>
      organizations?.find((organization) => organization.slug === orgSlug) ??
      null,
    [orgSlug, organizations],
  );

  useEffect(() => {
    if (!selectedOrganization) {
      return;
    }
    if (selectedOrganization.id !== selectedOrganizationId) {
      setSelectedOrganizationId(selectedOrganization.id);
      organizationContext.setSelectedOrganizationId(selectedOrganization.id);
    }
  }, [selectedOrganization, selectedOrganizationId]);

  useEffect(() => {
    return organizationContext.subscribe((organizationId) => {
      setSelectedOrganizationId(organizationId);
    });
  }, []);

  const [settingsOpen, setSettingsOpen] = useState(false);
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(
    null,
  );
  const projects = useQuery(api.projects.listForUser, {});
  const orgBillingState = useQuery(
    api.billing.getOrganizationBillingState,
    selectedOrganization
      ? { organizationId: selectedOrganization.id as Id<"workspaces"> }
      : "skip",
  );

  const filteredProjects = useMemo(() => {
    if (!projects) return [];
    if (!selectedOrganizationId) return projects;
    return projects.filter(
      (project) => project.organizationId === selectedOrganizationId,
    );
  }, [projects, selectedOrganizationId]);

  const isLoading = projects === undefined;
  const isEmpty = !isLoading && filteredProjects.length === 0;

  const isLicensed =
    orgBillingState != null && isOrgBillingActive(orgBillingState);
  const showBillingBlockedBanner =
    search.billing === "inactive" || (orgBillingState != null && !isLicensed);

  return (
    <div className="h-full overflow-x-hidden overflow-y-auto">
      <div className="mx-auto max-w-5xl px-4 py-6 sm:px-6 lg:px-8">
        {organizations !== undefined && !selectedOrganization ? (
          <div className="flex items-center justify-center py-16 text-sm text-muted-foreground">
            Organization not found.
          </div>
        ) : null}
        {organizations !== undefined && !selectedOrganization ? null : (
          <>
            {showBillingBlockedBanner ? (
              <div className="mb-4 flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-700/70 dark:bg-amber-900/20 dark:text-amber-200">
                <AlertTriangle className="mt-0.5 size-4 shrink-0" />
                <div>
                  <p className="font-medium">Project access paused</p>
                  <p>
                    Your account no longer has an active trial or subscription.
                    If you are an organization owner, open{" "}
                    <button
                      type="button"
                      className="cursor-pointer underline underline-offset-2 hover:text-amber-950 dark:hover:text-amber-100"
                      onClick={() =>
                        globalThis.dispatchEvent(
                          new CustomEvent("mdsync:open-org-settings"),
                        )
                      }
                    >
                      organization settings
                    </button>{" "}
                    to manage billing.
                  </p>
                </div>
              </div>
            ) : null}
            {!showBillingBlockedBanner &&
              orgBillingState?.status === "trial" &&
              typeof orgBillingState.trialEndsAt === "number" &&
              orgBillingState.trialEndsAt > Date.now() && (
                <div
                  className={`mb-4 flex items-start gap-2 rounded-md border px-3 py-2 text-sm ${
                    trialDaysLeft(orgBillingState.trialEndsAt) <= 3
                      ? "border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-700/70 dark:bg-amber-900/20 dark:text-amber-200"
                      : "border-blue-300 bg-blue-50 text-blue-900 dark:border-blue-700/70 dark:bg-blue-900/20 dark:text-blue-200"
                  }`}
                >
                  <Clock className="mt-0.5 size-4 shrink-0" />
                  <p>
                    Your free trial ends{" "}
                    {formatTrialRemaining(orgBillingState.trialEndsAt)}. Open{" "}
                    <button
                      type="button"
                      className="cursor-pointer underline underline-offset-2 hover:opacity-80"
                      onClick={() =>
                        globalThis.dispatchEvent(
                          new CustomEvent("mdsync:open-org-settings"),
                        )
                      }
                    >
                      organization settings
                    </button>{" "}
                    to subscribe.
                  </p>
                </div>
              )}
            {/* Page header */}
            <div className="mb-6 flex items-center justify-between">
              <h1 className="text-lg font-semibold">Projects</h1>
              {isLicensed && (
                <Button asChild size="sm">
                  <Link
                    to={newProjectRoute}
                    params={{ orgSlug: selectedOrganization?.slug ?? orgSlug }}
                  >
                    <Plus className="size-4" />
                    New Project
                  </Link>
                </Button>
              )}
            </div>

            {/* Content */}
            {isLoading ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="size-5 animate-spin text-muted-foreground" />
              </div>
            ) : isEmpty ? (
              <EmptyState
                licensed={isLicensed}
                organizationSlug={selectedOrganization?.slug ?? orgSlug}
              />
            ) : (
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
                {filteredProjects.map((project) => (
                  <ProjectCard
                    key={project.id}
                    project={project}
                    onOpenSettings={(projectId) => {
                      setSelectedProjectId(projectId);
                      setSettingsOpen(true);
                    }}
                    onLeaveProject={(projectId) => {
                      void leaveProject({
                        projectId: projectId as Id<"projects">,
                      }).catch((error) => {
                        toast.error(
                          formatUserFacingError(
                            error,
                            "Failed to leave project.",
                          ),
                        );
                      });
                    }}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>
      <ProjectSettings
        projectId={selectedProjectId}
        open={settingsOpen}
        onOpenChange={(open) => {
          setSettingsOpen(open);
          if (!open) {
            setSelectedProjectId(null);
          }
        }}
      />
    </div>
  );
};
